import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function AdoptACow() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cows, setCows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [settings, setSettings] = useState({});

  useEffect(() => {
    API.get('/cows').then(r => { setCows(r.data); setLoading(false); }).catch(() => setLoading(false));
    API.get('/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const defaultCowImageUrl = settings.defaultCowImage
    ? (settings.defaultCowImage.startsWith('http') ? settings.defaultCowImage : `${API_BASE}${settings.defaultCowImage}`)
    : null;

  const filtered = filter === 'available' ? cows.filter(c => !c.isAdopted) : filter === 'adopted' ? cows.filter(c => c.isAdopted) : cows;

  const handleAdopt = (cow) => {
    if (!user) { toast.info(t('adopt.toastLoginToAdopt')); navigate('/login'); return; }
    navigate('/donations', { state: { purpose: 'cow_adoption', cowId: cow._id, amount: cow.adoptionFee, cowName: cow.name } });
  };

  const healthColor = h => ({ healthy: 'var(--green)', sick: '#c62828', recovering: '#f57f17', critical: '#b71c1c' }[h] || '#888');

  const infoItems = [
    { icon: '📜', title: t('adopt.certTitle'), desc: t('adopt.certDesc') },
    { icon: '📸', title: t('adopt.updatesTitle'), desc: t('adopt.updatesDesc') },
    { icon: '🙏', title: t('adopt.blessingsTitle'), desc: t('adopt.blessingsDesc') },
    { icon: '💰', title: t('adopt.feeTitle'), desc: t('adopt.feeDesc') },
  ];

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <h1>{t('adopt.pageTitle')}</h1>
        <p>{t('adopt.pageSubtitle')}</p>
      </div>

      <section className="section">
        <div className="container">
          {/* Info Banner */}
          <div style={{ background: '#FFF8F0', border: '1.5px solid #FFE0B2', borderRadius: 12, padding: '20px 28px', marginBottom: 32, display: 'flex', gap: 40, flexWrap: 'wrap' }}>
            {infoItems.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: '1 1 200px' }}>
                <span style={{ fontSize: '1.6rem' }}>{b.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{b.title}</div>
                  <div style={{ fontSize: '0.82rem', color: '#777', lineHeight: 1.5 }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Filter */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
            {['all', 'available', 'adopted'].map(f => (
              <button key={f} className={`btn btn-sm${filter === f ? ' btn-primary' : ' btn-outline'}`} onClick={() => setFilter(f)}>
                {f === 'all' ? t('adopt.filterAll') : f === 'available' ? t('adopt.filterAvailable') : t('adopt.filterAdopted')}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#888' }}>
              <div style={{ fontSize: '4rem', marginBottom: 12 }}>🐂</div>
              <p>{t('adopt.noCowsFound')}</p>
            </div>
          ) : (
            <div className="grid-3">
              {filtered.map(cow => (
                <div key={cow._id} className="cow-card">
                  {cow.image
                    ? <img src={`${API_BASE}${cow.image}`} alt={cow.name} className="cow-card-img" />
                    : defaultCowImageUrl
                      ? <img src={defaultCowImageUrl} alt={cow.name} className="cow-card-img" />
                      : <div className="cow-card-img-placeholder">🐂</div>
                  }
                  {cow.isAdopted && <div className="cow-adopted-overlay">{t('adopt.alreadyAdopted')}</div>}
                  <div className="cow-card-body">
                    <div className="cow-card-name">{cow.name}</div>
                    <div className="cow-card-breed">{cow.breed} {cow.age ? `• ${cow.age} ${t('adopt.yearsAbbrev')}` : ''} {cow.color ? `• ${cow.color}` : ''}</div>
                    <div className="cow-card-badges">
                      <span className="badge" style={{ background: healthColor(cow.healthStatus) + '20', color: healthColor(cow.healthStatus) }}>
                        {cow.healthStatus}
                      </span>
                      <span className="badge badge-brown">{cow.gender}</span>
                      {cow.tagNumber && <span className="badge badge-orange">#{cow.tagNumber}</span>}
                    </div>
                    {cow.story && <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 12, lineHeight: 1.5 }}>{cow.story.slice(0, 100)}...</p>}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <span style={{ fontWeight: 700, color: 'var(--saffron)', fontSize: '1.1rem' }}>₹{(cow.adoptionFee || 1100).toLocaleString('en-IN')}/yr</span>
                    </div>
                    <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleAdopt(cow)}>
                      {t('adopt.adoptButton', { name: cow.name })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
